import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  CircleDot, Plus, Search, RefreshCw, AlertTriangle, CheckCircle,
  Truck, Settings, ArrowRight, Package, Edit, Trash2, Eye,
  TrendingUp, Calendar, DollarSign, Wrench, BarChart3, Filter } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI } from '../hooks/useFleetData';
import {
  SemiTruckSVG,
  BoxTruckSVG,
  CargoVanSVG,
  PickupTruckSVG } from
'../components/VehicleIllustrations';

const API_URL = API_BASE;

const conditionColors = {
  NEW: 'bg-emerald-100 text-emerald-700',
  GOOD: 'bg-green-100 text-green-700',
  FAIR: 'bg-yellow-100 text-yellow-700',
  WORN: 'bg-orange-100 text-orange-700',
  CRITICAL: 'bg-red-100 text-red-700'
};

const TireManagementPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('inventory');
  const [tireInventory, setTireInventory] = useState([]);
  const [vehicleTires, setVehicleTires] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [inventory, vehicleList] = await Promise.all([
      fetchAPI('/tires/inventory'),
      fetchAPI('/vehicles')]
      );
      setTireInventory(inventory);
      setVehicles(vehicleList);

      // Load tires for each vehicle
      const tiresMap = {};
      for (const v of vehicleList.slice(0, 10)) {// Limit to first 10 for performance
        try {
          const tires = await fetchAPI(`/vehicles/${v.id}/tires`);
          tiresMap[v.id] = tires;
        } catch (e) {
          tiresMap[v.id] = [];
        }
      }
      setVehicleTires(tiresMap);
    } catch (err) {
      console.error('Failed to load tire data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const totalTires = tireInventory.reduce((sum, t) => sum + (t.quantity_on_hand || 0), 0);
  const lowStockTires = tireInventory.filter((t) => (t.quantity_on_hand || 0) <= (t.reorder_point || 0));
  const totalValue = tireInventory.reduce((sum, t) => sum + (t.quantity_on_hand || 0) * (t.unit_price || 0), 0);

  const filteredInventory = tireInventory.filter((t) =>
  t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  t.part_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  t.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="tire-management-title">{t("tireManagement", "Tire Management")}

          </h1>
          <p className="text-gray-500">{t("trackTireInventory", "Track tire inventory, installations, and replacements")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search tires..."
              className="pl-9 w-64 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            
          </div>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowAddModal(true)}>
            
            <Plus className="w-4 h-4 mr-2" />{t("addTire", "Add Tire")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("totalInStock", "Total in Stock")}</p>
                <p className="text-2xl font-bold text-gray-900">{totalTires}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CircleDot className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("lowStockAlert", "Low Stock Alert")}</p>
                <p className="text-2xl font-bold text-red-600">{lowStockTires.length}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("tireTypes", "Tire Types")}</p>
                <p className="text-2xl font-bold text-gray-900">{tireInventory.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("inventoryValue", "Inventory Value")}</p>
                <p className="text-2xl font-bold text-green-600">{t("r", "R$")}{totalValue.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white p-1 rounded-lg shadow-sm">
          <TabsTrigger value="inventory" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Package className="w-4 h-4 mr-2" />{t("tireInventory", "Tire Inventory")}

          </TabsTrigger>
          <TabsTrigger value="vehicles" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Truck className="w-4 h-4 mr-2" />{t("vehicleTires", "Vehicle Tires")}

          </TabsTrigger>
          <TabsTrigger value="replacements" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Wrench className="w-4 h-4 mr-2" />{t("replacementsDue", "Replacements Due")}

          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CircleDot className="w-5 h-5 text-blue-500" />{t("tireInventory", "Tire Inventory")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ?
              <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div> :
              filteredInventory.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <CircleDot className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noTiresIn", "No Tires in Inventory")}</h3>
                  <p>{t("addTiresTo", "Add tires to start tracking")}</p>
                  <Button
                  className="mt-4 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => setShowAddModal(true)}>
                  
                    <Plus className="w-4 h-4 mr-2" />{t("addTire", "Add Tire")}

                </Button>
                </div> :

              <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("tire", "Tire")}</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("brand", "Brand")}</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-500">{t("specifications", "Specifications")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("inStock", "In Stock")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("reorderPoint", "Reorder Point")}</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-500">{t("unitPrice", "Unit Price")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("status", "Status")}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-500">{t("actions", "Actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((tire) => {
                      const isLowStock = (tire.quantity_on_hand || 0) <= (tire.reorder_point || 0);
                      return (
                        <tr key={tire.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <CircleDot className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{tire.name}</p>
                                  <p className="text-xs text-gray-500">{tire.part_number}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600">{tire.brand || '-'}</td>
                            <td className="py-3 px-4 text-gray-600 text-sm">
                              {tire.specifications ?
                            <span>{tire.specifications.size || tire.specifications}</span> :
                            '-'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-bold ${isLowStock ? 'text-red-600' : 'text-gray-900'}`}>
                                {tire.quantity_on_hand || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center text-gray-600">
                              {tire.reorder_point || 0}
                            </td>
                            <td className="py-3 px-4 text-right font-medium">{t("r", "R$")}
                            {(tire.unit_price || 0).toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {isLowStock ?
                            <Badge className="bg-red-100 text-red-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" />{t("lowStock", "Low Stock")}

                            </Badge> :

                            <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />{t("ok", "OK")}

                            </Badge>
                            }
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <Button variant="ghost" size="sm">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>);

                    })}
                    </tbody>
                  </table>
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicle Tires Tab */}
        <TabsContent value="vehicles">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-amber-500" />{t("tiresByVehicle", "Tires by Vehicle")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noVehicles", "No Vehicles")}</h3>
                  <p>{t("addVehiclesTo", "Add vehicles to track their tires")}</p>
                </div> :

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {vehicles.slice(0, 6).map((vehicle) =>
                <VehicleTireCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  tires={vehicleTires[vehicle.id] || []}
                  onViewDetails={() => setSelectedVehicle(vehicle)} />

                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* Replacements Due Tab */}
        <TabsContent value="replacements">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-orange-500" />{t("tireReplacementsDue", "Tire Replacements Due")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-gray-900">{t("noReplacementsDue", "No Replacements Due")}</h3>
                <p>{t("allTiresAre", "All tires are within their service life. Check back later.")}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Tire Modal */}
      {showAddModal &&
      <AddTireModal
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadData();
        }} />

      }
    </div>);

};

// Vehicle Tire Card Component
const VehicleTireCard = ({ vehicle, tires, onViewDetails }) => {const { t } = useTranslation("fleet");
  const VehicleIcon = vehicle.vehicle_type?.toLowerCase().includes('semi') ? SemiTruckSVG :
  vehicle.vehicle_type?.toLowerCase().includes('box') ? BoxTruckSVG :
  vehicle.vehicle_type?.toLowerCase().includes('van') ? CargoVanSVG :
  PickupTruckSVG;

  return (
    <div className="border rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-bold text-gray-900">{vehicle.vehicle_code}</h3>
          <p className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</p>
          <p className="text-xs text-gray-400">{vehicle.license_plate}</p>
        </div>
        <Badge className="bg-blue-100 text-blue-700">
          {tires.length}{t("tires", "tires")}
        </Badge>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <VehicleIcon className="w-full h-20" fillColor="#ffffff" />
      </div>

      {/* Tire Positions Grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].map((pos, idx) => {
          const tire = tires.find((t) => t.position?.toLowerCase().includes(pos.toLowerCase().split(' ')[0]) &&
          t.position?.toLowerCase().includes(pos.toLowerCase().split(' ')[1]));
          return (
            <div key={pos} className={`p-2 rounded-lg text-center text-xs ${
            tire ? 'bg-green-50 border border-green-200' : 'bg-gray-100 border border-gray-200'}`
            }>
              <CircleDot className={`w-4 h-4 mx-auto mb-1 ${tire ? 'text-green-600' : 'text-gray-400'}`} />
              <p className="font-medium text-gray-700">{pos}</p>
              {tire ?
              <p className="text-green-600">{tire.current_health}%</p> :

              <p className="text-gray-400">{t("empty", "Empty")}</p>
              }
            </div>);

        })}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={onViewDetails}>
        
        <Eye className="w-4 h-4 mr-2" />{t("viewDetails", "View Details")}

      </Button>
    </div>);

};

// Add Tire Modal
const AddTireModal = ({ onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    part_number: '',
    name: '',
    brand: '',
    category: 'TIRES',
    unit_price: '',
    min_stock_level: 4,
    reorder_point: 8,
    reorder_quantity: 10,
    specifications: '',
    alert_on_low_stock: true
  });
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchAPI('/parts', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
          specifications: formData.specifications ? { size: formData.specifications } : null
        })
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create tire:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-lg m-4 bg-white" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <CircleDot className="w-5 h-5 text-blue-500" />{t("addNewTire", "Add New Tire")}

          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("partNumber", "Part Number *")}</label>
                <Input
                  value={formData.part_number}
                  onChange={(e) => setFormData((f) => ({ ...f, part_number: e.target.value }))}
                  placeholder="e.g., TIRE-215-75R17"
                  required />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("brand", "Brand")}</label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData((f) => ({ ...f, brand: e.target.value }))}
                  placeholder="e.g., Bridgestone" />
                
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("namedescription", "Name/Description *")}</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Bridgestone 215/75R17.5 Duravis"
                required />
              
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("sizespecifications", "Size/Specifications")}</label>
              <Input
                value={formData.specifications}
                onChange={(e) => setFormData((f) => ({ ...f, specifications: e.target.value }))}
                placeholder="e.g., 215/75R17.5" />
              
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("unitPriceR", "Unit Price (R$)")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))}
                  placeholder="850.00" />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("reorderPoint", "Reorder Point")}</label>
                <Input
                  type="number"
                  value={formData.reorder_point}
                  onChange={(e) => setFormData((f) => ({ ...f, reorder_point: parseInt(e.target.value) }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("reorderQty", "Reorder Qty")}</label>
                <Input
                  type="number"
                  value={formData.reorder_quantity}
                  onChange={(e) => setFormData((f) => ({ ...f, reorder_quantity: parseInt(e.target.value) }))} />
                
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.alert_on_low_stock}
                onChange={(e) => setFormData((f) => ({ ...f, alert_on_low_stock: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-red-500" />
              
              <label className="text-sm text-gray-700">{t("alertWhenStock", "Alert when stock is low")}</label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}>
                
                {loading ?
                <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />{t("creating", "Creating...")}

                </> :

                <>
                    <Plus className="w-4 h-4 mr-2" />{t("addTire", "Add Tire")}

                </>
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

export default TireManagementPage;